import type { IconName } from "./iconNames.ts";

/**
 * Shared catalogs - and THREE different kinds of string, which is worth knowing before you
 * "finish the i18n migration" in here (round 122; round 121 filed this file as half-converted,
 * which it never was):
 *
 *  1. **Owner chrome** - text the owner reads in the app. It travels as a **`t()` key**, like
 *     everything else since round 70: `DISCOVERY_PACKS[].labelKey`, `templateVariableDescKey()`,
 *     `RELATED_CONTENT_TYPE_LABEL_KEYS`. The English lives in `locales/en.json` with every other
 *     sentence, not here.
 *  2. **Outlet-language words** - `SITE_TYPE_LABELS` and friends, which look like a stalled
 *     UI translation and are nothing of the sort: they are indexed by the language the PITCH
 *     is being written in (`languageToLocale(getEmailLanguage(blog))`), a different axis with
 *     26 possible values. See the `OutletLanguageWords` note below.
 *  3. **Wire data** - `GENRES`, `REGIONS`, `OUTLET_LANGUAGES`, `EPK_FONTS`, the brand names in
 *     `BIO_SOCIAL_LABELS`/`SMART_LINK_PLATFORMS`. These are VALUES: stored in columns, sent to
 *     the scraper, written into a fonts.googleapis.com URL, matched against an API's vocabulary.
 *     Translating one would break the thing that reads it. They stay English and are not chrome.
 *
 * The reason kind 1 sat here in English for twenty rounds of i18n work is that **a shared data
 * module is invisible to the audit**: `npm run i18n:audit` reads `src/frontend` for literals in
 * RENDERED positions, and the old `${TEMPLATE_VARIABLES[key]}` was an interpolation, which the
 * scanner strips before it looks. Class 5 of the audit now reads this file directly (a string
 * literal on a label-ish property anywhere in `shared/**`), so kind 1 cannot silently come back -
 * mark a deliberate kind-2/3 string with `i18n-ignore` and say why.
 */
export const SITE_TYPES_ARRAY = [
	"blog",
	"radio_station",
	"magazine",
	"platform",
	"podcast",
	"youtube_channel",
	"playlist_curator",
	"newsletter",
	// Short-form creators (TikTok/Reels/Shorts accounts - edit pages, dancers, curation pages).
	// The modern blog: pitching them the SOUND (not a review) is what actually spreads a song.
	"creator",
	// §I.5's sync lane (round 255). TWO members rather than one, because they are two readers
	// asking different questions: a music SUPERVISOR places one song in one scene and their first
	// question is clearance, while a music LIBRARY takes a track into a catalog and their first
	// question is terms. A single "sync" type would have to pick one of those pitches for both.
	//
	// ⚠ They are the first outlet class in this app that is not pitched against a RELEASE DATE. A
	// blog covers news; a supervisor answers a brief, and a song from four years ago is exactly as
	// placeable as one out next month. Nothing here encodes that - it is why `defaultWavePlan`
	// deliberately does not list them (see `services/waves.ts`) and why the campaign's ordinary
	// continuous sweep is the right lane for them.
	"sync_supervisor",
	"music_library"
] as const;

export const SITE_TYPES = new Set(SITE_TYPES_ARRAY);

/**
 * The outlet classes whose pitch is a SYNC pitch (§I.5). Declared as a subset of the array above -
 * `satisfies` makes a typo a compile error rather than a lane that quietly matches nobody.
 *
 * ⚠ **What makes them one group is not the word "sync", it is that BOTH read the clearance line
 * first**, which is why a release that has not answered `rights_one_stop` may not be pitched to
 * either (see `services/syncReadiness.ts`) - and it is also why they are the app's only outlet class
 * with no release-date anchor, and therefore its only class the campaign's date-driven lanes cannot
 * reach. Two facts, one membership; a third member joins here and inherits both, so **read
 * `waves.ts` and `syncReadiness.ts` before adding one.**
 */
export const SYNC_SITE_TYPES = ["sync_supervisor", "music_library"] as const satisfies readonly (typeof SITE_TYPES_ARRAY)[number][];

/**
 * A DRAWING per outlet type (round 244, BACKLOG tier 2 item 20).
 *
 * The owner's ask, of the outlet editor's Site type field: *"I'd love to be able to assign
 * illustrations to each outlet type and show it next to each"*. A site type is a category with no
 * picture of its own, which is exactly the list a native `<option>` cannot serve - it holds one run
 * of text and nothing else.
 *
 * ⚠ **A closed `Record` over the type union, so a new site type cannot ship without a mark.** The
 * alternative - a lookup with a fallback - is the shape that goes half-filled and nobody notices,
 * because a missing drawing renders as a gap rather than as an error. Adding a member to
 * `SITE_TYPES_ARRAY` fails the typecheck here until it is answered, which is the same bargain
 * `TABLE_DISPOSITIONS` makes one lane over.
 *
 * ⚠ **`IconName`, never a glyph and never a path.** The drawings are APP ONLY (`src/frontend/icons.ts`
 * may not cross into a published-page renderer) and the NAMES are what `shared/` is allowed to hold -
 * that split is `shared/iconNames.ts`'s whole reason for existing, and going around it here would be
 * the second glyph vocabulary this app has spent two rounds avoiding.
 */
export const SITE_TYPE_ICONS: Record<(typeof SITE_TYPES_ARRAY)[number], IconName> = {
	blog: "write",
	// Broadcast, not a microphone: the mic is the podcast's mark, and two lists sharing one drawing
	// is worse than either of them having a slightly abstract one.
	radio_station: "waves",
	magazine: "newspaper",
	platform: "globe",
	podcast: "mic",
	youtube_channel: "brand-youtube",
	// A playlist is a LIST of songs, and that is what a curator hands you.
	playlist_curator: "list",
	newsletter: "mail",
	// Short-form video: the reel, not the camera that shot it.
	creator: "film",
	// The SCREEN the song ends up on, not the person - a supervisor's whole job is the picture, and
	// `film` is already the creator's mark one row up.
	sync_supervisor: "clapper",
	// A catalog somebody licenses out of: a box of records rather than a list of them, which is
	// `list`'s job for the playlist curator.
	music_library: "archive"
};

/**
 * The four platforms this app CONNECTS to, and the BRAND mark for each.
 *
 * ⚠ **These are brand marks, not the house drawings the same idea becomes elsewhere** - `play` is a
 * verb in a button label and `brand-youtube` here, `users` is a follower count on a chip and
 * `brand-facebook` here. A platform list is the one place a reader is asked to recognize a company
 * rather than read a word, which is what the second visual voice in `icons.ts` is for.
 *
 * ⚠ **It lives in `shared/` because there were THREE copies of it** (`pages/content.ts`,
 * `pages/dashboard.ts` and, the round this moved, a fourth about to be written in `pages/runway.ts`)
 * - and dashboard's held only three of the four with a `phone` fallback, so a Facebook attention row
 * was marked with a telephone. **A map that more than one surface needs is a fork the moment it is
 * copied**, and an `IconName` is exactly what `shared/iconNames.ts` exists to let `shared/` hold.
 * ⚠ It is a closed `Record` for `SITE_TYPE_ICONS`' reason: a fifth platform cannot ship unmarked,
 * because a missing drawing renders as a gap rather than as an error.
 *
 * The platform KEY is wire data (it is what the connectors, the queue and the beacon all say), so it
 * never goes through `t()`; the reader-facing name is a `content.platform.<key>` key.
 */
export const SOCIAL_PLATFORMS = ["instagram", "youtube", "tiktok", "facebook"] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export const PLATFORM_ICONS: Record<SocialPlatform, IconName> = {
	instagram: "brand-instagram",
	youtube: "brand-youtube",
	tiktok: "brand-tiktok",
	facebook: "brand-facebook"
};

/**
 * What to CALL each platform - and ⚠ **these are proper nouns and never go through `t()`**, the same
 * rule `shared/brand.ts` states for this product's own name: a Danish UI writing "Instagram" is not
 * translating anything, and a locale file that could disagree is a locale file that can be wrong.
 * (`content.platform.*` exists as a key family for the same four words and holds the identical string
 * in every locale; it is not worth a sweep, but nothing new should join it.)
 */
// i18n-ignore - brand names, see above
export const PLATFORM_LABELS: Record<SocialPlatform, string> = {
	instagram: "Instagram",
	youtube: "YouTube",
	tiktok: "TikTok",
	facebook: "Facebook"
};

/**
 * Where a clip got POSTED - which is a wider question than which platforms the app connects to, and
 * that is the whole reason it is a second list. Snapchat is a real place a reel goes and there is no
 * connector for it; `other` exists because this list will never be complete.
 *
 * ⚠ **It moved here because there were THREE copies and only one of them was the law.** The zod enum
 * in `services/clipPosts.ts` is what `POST /api/releases/:id/clip-posts` actually accepts;
 * `pages/shortsStudio.ts` kept its own `POST_PLATFORM_KEYS` array of the same six, and
 * `pages/runway.ts` built the list inline from `SOCIAL_PLATFORMS` plus "other" - **so two pickers
 * offered different answers to one endpoint's one question**, and a rung posted to Snapchat could
 * only be filed as "somewhere else" while the clip journal one screen over recorded it properly.
 * That is round 245's finding in a third lane: a union and an array of the same set is a fork
 * whether or not either is wrong.
 *
 * ⚠ The keys are WIRE DATA (they are what the `clip_posts` rows hold), so they never go through
 * `t()`; only the catch-all's label is chrome, which is why it is absent from the names below rather
 * than sitting in them as an English word.
 */
export const POST_PLATFORMS = ["tiktok", "instagram", "youtube", "facebook", "snapchat", "other"] as const;
export type PostPlatform = (typeof POST_PLATFORMS)[number];

/**
 * The brand mark per posting platform - closed for `SITE_TYPE_ICONS`' reason: a seventh place cannot
 * ship unmarked, because a missing drawing renders as a gap rather than as an error. The four the app
 * connects to are SPREAD rather than re-typed, so this map cannot disagree with `PLATFORM_ICONS`.
 * `other` gets the globe, which is the same answer the runway's picker already gave it.
 */
export const POST_PLATFORM_ICONS: Record<PostPlatform, IconName> = {
	...PLATFORM_ICONS,
	snapchat: "brand-snapchat",
	other: "globe"
};

// i18n-ignore - brand names, the same call PLATFORM_LABELS makes
export const POST_PLATFORM_NAMES: Record<Exclude<PostPlatform, "other">, string> = {
	...PLATFORM_LABELS,
	snapchat: "Snapchat"
};

/**
 * A word that goes INSIDE a pitch, in the language that pitch is written in - NOT app chrome.
 *
 * The distinction has teeth because the shape is identical to a UI dictionary and the axis is not:
 * these are indexed by `languageToLocale(getEmailLanguage(blog))`, the OUTLET's language (26
 * possible values, see `utils/localize.ts`), so a Danish radio station gets "radiostation" in a
 * Danish pitch while the owner's UI locale is irrelevant. Converting them to `t()` keys would be a
 * category error: `t()` answers in the READER's language, and the reader here is a stranger being
 * pitched, not the owner. (`epkStrings` is the same axis for the published pages.)
 *
 * ⚠ The honest gap this note used to describe - 2 of 26 languages, so a German outlet read the
 * English word inside an otherwise German pitch - is CLOSED for `SITE_TYPE_LABELS` (2026-08-05) and
 * open for the two tables below it, for a reason worth knowing: **nothing reads them.** See their
 * own warning.
 */
export type OutletLanguageWords = Record<string, Record<string, string>>;

/**
 * What to call an outlet of each site type, in the pitch's own language.
 *
 * ⚠ **This is the one table with a real reader** - `emailComposer.ts` puts it in `{{site_type}}`, so
 * every one of these words has been printed into mail that reaches a stranger. All 26 languages of
 * `LANG_TO_LOCALE` are filled and `tests/outletLanguageWords.test.ts` keeps them that way: a new site
 * type or a new outlet language that arrives without its words does not degrade quietly, it fails.
 * That gate is the point of the round rather than the words - **the words were missing for 122
 * rounds precisely because nothing could notice**, since the reader falls back to English and a
 * fallback is indistinguishable from a translation nobody has read.
 *
 * ⚠ **They are pitch vocabulary, not chrome**: keep them lowercase where the language writes common
 * nouns lowercase, keep the loanword where the industry uses the loanword ("podcast" is "podcast" in
 * most of these), and never let one become a `t()` key - see the note above.
 */
export const SITE_TYPE_LABELS: OutletLanguageWords = {
	blog: {
		en: "blog",
		da: "blog",
		de: "Blog",
		fr: "blog",
		nl: "blog",
		es: "blog",
		it: "blog",
		pt: "blog",
		sv: "blogg",
		no: "blogg",
		fi: "blogi",
		pl: "blog",
		cs: "blog",
		hu: "blog",
		ro: "blog",
		bg: "блог",
		el: "ιστολόγιο",
		tr: "blog",
		ar: "مدونة",
		ja: "ブログ",
		ko: "블로그",
		zh: "博客",
		th: "บล็อก",
		vi: "blog",
		id: "blog",
		is: "blogg"
	},
	radio_station: {
		en: "radio station",
		da: "radiostation",
		de: "Radiosender",
		fr: "station de radio",
		nl: "radiostation",
		es: "emisora de radio",
		it: "stazione radio",
		pt: "estação de rádio",
		sv: "radiostation",
		no: "radiostasjon",
		fi: "radioasema",
		pl: "stacja radiowa",
		cs: "rozhlasová stanice",
		hu: "rádióállomás",
		ro: "post de radio",
		bg: "радиостанция",
		el: "ραδιοφωνικός σταθμός",
		tr: "radyo istasyonu",
		ar: "محطة إذاعية",
		ja: "ラジオ局",
		ko: "라디오 방송국",
		zh: "电台",
		th: "สถานีวิทยุ",
		vi: "đài phát thanh",
		id: "stasiun radio",
		is: "útvarpsstöð"
	},
	magazine: {
		en: "online magazine",
		da: "onlinemagasin",
		de: "Onlinemagazin",
		fr: "magazine en ligne",
		nl: "onlinemagazine",
		es: "revista online",
		it: "rivista online",
		pt: "revista online",
		sv: "onlinemagasin",
		no: "nettmagasin",
		fi: "verkkolehti",
		pl: "magazyn internetowy",
		cs: "online magazín",
		hu: "online magazin",
		ro: "revistă online",
		bg: "онлайн списание",
		el: "διαδικτυακό περιοδικό",
		tr: "çevrimiçi dergi",
		ar: "مجلة إلكترونية",
		ja: "オンラインマガジン",
		ko: "온라인 매거진",
		zh: "在线杂志",
		th: "นิตยสารออนไลน์",
		vi: "tạp chí trực tuyến",
		id: "majalah online",
		is: "nettímarit"
	},
	platform: {
		en: "platform",
		da: "platform",
		de: "Plattform",
		fr: "plateforme",
		nl: "platform",
		es: "plataforma",
		it: "piattaforma",
		pt: "plataforma",
		sv: "plattform",
		no: "plattform",
		fi: "alusta",
		pl: "platforma",
		cs: "platforma",
		hu: "platform",
		ro: "platformă",
		bg: "платформа",
		el: "πλατφόρμα",
		tr: "platform",
		ar: "منصة",
		ja: "プラットフォーム",
		ko: "플랫폼",
		zh: "平台",
		th: "แพลตฟอร์ม",
		vi: "nền tảng",
		id: "platform",
		is: "vettvangur"
	},
	podcast: {
		en: "podcast",
		da: "podcast",
		de: "Podcast",
		fr: "podcast",
		nl: "podcast",
		es: "podcast",
		it: "podcast",
		pt: "podcast",
		sv: "podd",
		no: "podkast",
		fi: "podcast",
		pl: "podcast",
		cs: "podcast",
		hu: "podcast",
		ro: "podcast",
		bg: "подкаст",
		el: "podcast",
		tr: "podcast",
		ar: "بودكاست",
		ja: "ポッドキャスト",
		ko: "팟캐스트",
		zh: "播客",
		th: "พอดแคสต์",
		vi: "podcast",
		id: "podcast",
		is: "hlaðvarp"
	},
	youtube_channel: {
		en: "YouTube channel",
		da: "YouTube-kanal",
		de: "YouTube-Kanal",
		fr: "chaîne YouTube",
		nl: "YouTube-kanaal",
		es: "canal de YouTube",
		it: "canale YouTube",
		pt: "canal do YouTube",
		sv: "YouTube-kanal",
		no: "YouTube-kanal",
		fi: "YouTube-kanava",
		pl: "kanał YouTube",
		cs: "kanál na YouTube",
		hu: "YouTube-csatorna",
		ro: "canal de YouTube",
		bg: "YouTube канал",
		el: "κανάλι YouTube",
		tr: "YouTube kanalı",
		ar: "قناة يوتيوب",
		ja: "YouTubeチャンネル",
		ko: "유튜브 채널",
		zh: "YouTube 频道",
		th: "ช่อง YouTube",
		vi: "kênh YouTube",
		id: "kanal YouTube",
		is: "YouTube-rás"
	},
	playlist_curator: {
		en: "playlist curator",
		da: "playlistekurator",
		de: "Playlist-Kurator",
		fr: "curateur de playlists",
		nl: "playlistcurator",
		es: "curador de playlists",
		it: "curatore di playlist",
		pt: "curador de playlists",
		sv: "spellistkurator",
		no: "spillelistekurator",
		fi: "soittolistakuraattori",
		pl: "kurator playlist",
		cs: "kurátor playlistů",
		hu: "lejátszásilista-kurátor",
		ro: "curator de playlisturi",
		bg: "куратор на плейлисти",
		el: "επιμελητής playlist",
		tr: "çalma listesi küratörü",
		ar: "منسق قوائم تشغيل",
		ja: "プレイリストキュレーター",
		ko: "플레이리스트 큐레이터",
		zh: "歌单策展人",
		th: "ผู้จัดเพลย์ลิสต์",
		vi: "người tuyển chọn playlist",
		id: "kurator playlist",
		is: "umsjónarmaður lagalista"
	},
	newsletter: {
		en: "newsletter",
		da: "nyhedsbrev",
		de: "Newsletter",
		fr: "newsletter",
		nl: "nieuwsbrief",
		es: "boletín",
		it: "newsletter",
		pt: "newsletter",
		sv: "nyhetsbrev",
		no: "nyhetsbrev",
		fi: "uutiskirje",
		pl: "newsletter",
		cs: "newsletter",
		hu: "hírlevél",
		ro: "newsletter",
		bg: "бюлетин",
		el: "newsletter",
		tr: "bülten",
		ar: "نشرة بريدية",
		ja: "ニュースレター",
		ko: "뉴스레터",
		zh: "邮件通讯",
		th: "จดหมายข่าว",
		vi: "bản tin",
		id: "buletin",
		is: "fréttabréf"
	},
	creator: {
		en: "short-form creator",
		da: "kortformat-skaber",
		de: "Kurzvideo-Creator",
		fr: "créateur de contenus courts",
		nl: "maker van korte video's",
		es: "creador de vídeos cortos",
		it: "creator di video brevi",
		pt: "criador de vídeos curtos",
		sv: "kortformatskapare",
		no: "kortformatskaper",
		fi: "lyhytvideoiden tekijä",
		pl: "twórca krótkich form",
		cs: "tvůrce krátkých videí",
		hu: "rövidvideó-készítő",
		ro: "creator de videoclipuri scurte",
		bg: "създател на кратки видеа",
		el: "δημιουργός σύντομων βίντεο",
		tr: "kısa video içerik üreticisi",
		ar: "صانع محتوى قصير",
		ja: "ショート動画クリエイター",
		ko: "숏폼 크리에이터",
		zh: "短视频创作者",
		th: "ครีเอเตอร์คลิปสั้น",
		vi: "nhà sáng tạo video ngắn",
		id: "kreator video pendek",
		is: "höfundur stuttra myndbanda"
	},
	// ⚠ The loanword wins in more languages here than anywhere else in this table, and that is the
	// rule rather than laziness: "music supervisor" is the JOB TITLE printed in the credits of a
	// Danish, Swedish or Polish production, so translating it invents a word the reader does not use
	// for their own job. Where a language really does have its own term in the credits (French,
	// Spanish, Portuguese, Romanian, Czech), that term is what is here.
	sync_supervisor: {
		en: "music supervisor",
		da: "music supervisor",
		de: "Music Supervisor",
		fr: "superviseur musical",
		nl: "music supervisor",
		es: "supervisor musical",
		it: "supervisore musicale",
		pt: "supervisor musical",
		sv: "music supervisor",
		no: "music supervisor",
		fi: "music supervisor",
		pl: "music supervisor",
		cs: "hudební supervizor",
		hu: "zenei supervisor",
		ro: "supervizor muzical",
		bg: "музикален супервайзор",
		el: "μουσικός επιμελητής",
		tr: "müzik süpervizörü",
		ar: "مشرف موسيقي",
		// ⚠ NOT 音楽監督 / 음악 감독 / 音乐总监 - all three of those are "music DIRECTOR", who writes or
		// conducts the score. A supervisor licenses existing recordings, which is the person being
		// pitched here, and getting this wrong addresses the mail to a different job entirely.
		ja: "ミュージックスーパーバイザー",
		ko: "뮤직 슈퍼바이저",
		zh: "音乐监督",
		th: "มิวสิกซูเปอร์ไวเซอร์",
		vi: "giám sát âm nhạc",
		id: "music supervisor",
		is: "music supervisor"
	},
	music_library: {
		en: "music library",
		da: "musikbibliotek",
		de: "Musikbibliothek",
		fr: "bibliothèque musicale",
		nl: "muziekbibliotheek",
		es: "biblioteca musical",
		it: "libreria musicale",
		pt: "biblioteca musical",
		sv: "musikbibliotek",
		no: "musikkbibliotek",
		fi: "musiikkikirjasto",
		pl: "biblioteka muzyczna",
		cs: "hudební knihovna",
		hu: "zenei könyvtár",
		ro: "bibliotecă muzicală",
		bg: "музикална библиотека",
		el: "μουσική βιβλιοθήκη",
		tr: "müzik kütüphanesi",
		ar: "مكتبة موسيقية",
		ja: "音楽ライブラリー",
		ko: "음악 라이브러리",
		zh: "音乐库",
		th: "คลังเพลง",
		vi: "thư viện âm nhạc",
		id: "perpustakaan musik",
		is: "tónlistarsafn"
	}
};

/**
 * Fit-score thresholds (scoreFit returns 0-100). Below SKIP, a pairing is a clear
 * genre/style mismatch and is not composed at all (bulk + campaigns); below WARN,
 * it's a weak fit that still composes but gets flagged in the Queue.
 */
export const FIT_SKIP_THRESHOLD = 20;
export const FIT_WARN_THRESHOLD = 40;

/**
 * How many featured mentions a single published page shows on its social-proof wall (round 113).
 * A wall of a few is proof; a wall of forty is noise and byte weight on a page whose whole job is
 * to load fast. Shared so the server cap (`listFeaturedMentions`) and the Content page's "only your
 * most recent show" hint threshold can never disagree.
 */
export const FEATURED_WALL_LIMIT = 6;

/**
 * What to call an outlet's audience ("blog readers"), in the pitch's own language.
 *
 * ⚠ **NOTHING READS THIS TABLE, and `EDITOR_TYPE_LABELS` below has exactly one reader that asks for
 * ENGLISH on purpose** (measured 2026-08-05, while filling the sibling above): `scoreEmailTemplate`
 * in `services/ai.ts` puts `.en` into an AI system prompt, which this app writes in English whoever
 * the outlet is. That is why both stayed at English and Danish while the table with a real reader
 * was filled in 26 languages - **translating a table nobody reads in that language is 432 phrases
 * with no effect on any pitch**, and wiring one up is a design call rather than a translation job.
 * `tests/outletLanguageWords.test.ts` holds that disposition and fails if either grows a
 * locale-indexed reader.
 *
 * ⚠ **What kept them looking maintained is a test.** Two files assert their SHAPE - that every site
 * type has an English and a Danish entry - so adding a site type dutifully updates all three tables
 * and every gate stays green. **A shape assertion over dead data is the strongest possible disguise
 * for dead data**: it is round 73's "an extracted module with no consumer is invisible to every
 * check this repo runs", except louder, because here the checks pass on purpose. Before filling
 * either table, find its call site - with a NODE scan and not a grep, which is its own finding
 * (`tests/greppable.test.ts`).
 */
export const AUDIENCE_TYPE_LABELS: OutletLanguageWords = {
	blog: { en: "blog readers", da: "bloglæsere" },
	radio_station: { en: "radio listeners", da: "radiolyttere" },
	magazine: { en: "online magazine readers", da: "læsere af onlinemagasiner" },
	platform: { en: "platform users", da: "platformbrugere" },
	podcast: { en: "podcast listeners", da: "podcastlyttere" },
	youtube_channel: { en: "YouTube channel viewers", da: "YouTube-kanal seere" },
	playlist_curator: { en: "playlist listeners", da: "playlistelyttere" },
	newsletter: { en: "newsletter subscribers", da: "nyhedsbrevsabonnenter" },
	creator: { en: "followers", da: "følgere" },
	// ⚠ Neither of these outlets HAS an audience of its own, and the honest answer is one step removed
	// rather than an invented noun: a supervisor's reach is whoever watches the production the song
	// ends up in, and a library's is the productions that license out of the catalog. Kept to the
	// short noun this table's other rows use, because the entries slot into a sentence.
	sync_supervisor: { en: "viewers", da: "seere" },
	music_library: { en: "productions", da: "produktioner" }
};

/** What to call the person behind an outlet ("blog editors"), in the pitch's own language. */
export const EDITOR_TYPE_LABELS: OutletLanguageWords = {
	blog: { en: "blog editors", da: "blogredaktører" },
	radio_station: { en: "radio editors", da: "radioredaktører" },
	magazine: { en: "online magazine editors", da: "redaktører af onlinemagasiner" },
	platform: { en: "platform editors", da: "platformredaktører" },
	podcast: { en: "podcast editors", da: "podcastredaktører" },
	youtube_channel: { en: "YouTube channel editors", da: "YouTube-kanalredaktører" },
	playlist_curator: { en: "playlist curators", da: "playlistekuratorer" },
	newsletter: { en: "newsletter writers", da: "nyhedsbrevsskribenter" },
	creator: { en: "creators", da: "skabere" },
	sync_supervisor: { en: "music supervisors", da: "music supervisors" },
	music_library: { en: "music library managers", da: "musikbiblioteksansvarlige" }
};

/**
 * One-click discovery search packs for channels that respond to free pitches.
 *
 * Two kinds of string in one object, deliberately: `labelKey` is the button the OWNER reads (a
 * `t()` key, so the Discover page renders it in their language), while `queries` are search
 * strings sent to a web-search API and must stay English - they are what the index is asked, not
 * something anyone reads. The `{genre}`/`{region}` placeholders expand against the Discover page's
 * current selections (`expandPackQueries` in `shared/discoveryPacks.ts`), and the packs' own
 * native-language variants are generated by AI at click time, from these English seeds.
 */
export const DISCOVERY_PACKS: { id: string; labelKey: string; queries: string[] }[] = [
	{
		id: "college_radio",
		labelKey: "discover.pack.collegeRadio",
		queries: [
			"college radio station music submissions email",
			"university campus radio submit music demo",
			"community radio station new music submissions",
			"college radio music director submit track email",
			"non-commercial radio station accepting music submissions"
		]
	},
	{
		id: "internet_radio",
		labelKey: "discover.pack.internetRadio",
		queries: [
			"internet radio station submit music email",
			"online radio show music submissions independent artists",
			"web radio indie music submission email",
			"independent online radio station accepting demos",
			"radio show submit your track email free"
		]
	},
	{
		id: "youtube_channels",
		labelKey: "discover.pack.youtubeChannels",
		queries: [
			"YouTube music channel submissions email",
			"YouTube music curation channel submit track",
			"music review YouTube channel submissions",
			"YouTube channel promotes independent music submit email",
			"music discovery YouTube channel demo submission"
		]
	},
	{
		id: "soundcloud_repost",
		labelKey: "discover.pack.soundcloudRepost",
		queries: [
			"SoundCloud repost channel submit track free",
			"SoundCloud promotion network free submission email",
			"SoundCloud repost network demo submission",
			"SoundCloud curation collective submit music"
		]
	},
	{
		id: "zines",
		labelKey: "discover.pack.zines",
		queries: [
			"music zine submissions email",
			"independent music magazine submit music review",
			"DIY zine music reviews submission email",
			"online music magazine accepting submissions free",
			"underground music publication submit demo"
		]
	},
	{
		id: "newsletters",
		labelKey: "discover.pack.newsletters",
		queries: [
			"independent music newsletter submit music",
			"music discovery newsletter Substack submissions",
			"new music recommendations newsletter contact email",
			"music curator newsletter accepting submissions",
			"weekly music newsletter feature independent artists"
		]
	},
	{
		id: "podcasts",
		labelKey: "discover.pack.podcasts",
		queries: [
			"music podcast submit song email",
			"new music discovery podcast submissions",
			"independent artist interview podcast contact",
			"music review podcast accepting submissions",
			"indie music podcast feature your song"
		]
	},
	{
		id: "short_form_creators",
		labelKey: "discover.pack.shortFormCreators",
		queries: [
			"tiktok music curator submit song email",
			"instagram reels music promotion page contact email",
			"music edit page song submissions",
			"tiktok music discovery account submit music",
			"new music tiktok page business email",
			"{genre} tiktok creator music submissions"
		]
	},
	/**
	 * PLAYLIST CURATORS, found through their OWN submission pages (round 298, §AK item 3).
	 *
	 * ⚠ **This pack exists because the Spotify curator scraper's ceiling is about ten.** That lane
	 * (`services/spotify.ts`) can only see public playlists whose DESCRIPTION happens to carry an
	 * address, and it stores the pay-to-play ones disabled - the owner ran it against the hosted catalog
	 * on 2026-09-02 and ten curators is what it found. Independent curators who take free submissions
	 * mostly run a site, a form or a link page with a "submit your track" page of their own, which is
	 * exactly the shape every other pack here finds for its outlet class. Same audit as the packs above:
	 * every seed describes ONE curator's own submission page - never a round-up, a "best playlists" post
	 * or a list of curators, which is the database-right exposure `services/scraper.ts` bounds.
	 *
	 * ⚠ **The payola defense is the classifier's `isFree`**, unchanged: a page asking for a fee per
	 * submission is refused before it becomes a row, and `SKIP_DOMAINS` already keeps SubmitHub, Groover
	 * and Musosoup out of the fetch entirely. Expect dozens, not thousands - the curators who matter most
	 * sit behind those three, and the honest playlist lane for a hosted artist is still Pitch Studio's
	 * editorial pitch. This pack fills the free tail of the catalog, which is the part a cold pitch can
	 * reach at all.
	 */
	{
		id: "playlist_curators",
		labelKey: "discover.pack.playlistCurators",
		queries: [
			"spotify playlist curator submit your song free",
			"independent spotify playlist submissions form",
			"curated playlist send us your track email",
			"playlist curator submit music no fee",
			"apple music playlist curator submissions",
			"{genre} playlist curator submit song"
		]
	},
	/**
	 * §I.5's sync lane (round 255). Two packs, for the two readers - a supervisor places one song in
	 * one scene, a library takes a track into a catalog.
	 *
	 * ⚠ **Every seed here describes an outlet's OWN submission page, which is the audit
	 * `services/scraper.ts`'s database-right note performed on the eight packs above** - no query asks
	 * for a directory, a round-up or a "list of music supervisors", because taking a substantial part
	 * of somebody's curated list is the one exposure in this lane that is not about GDPR.
	 *
	 * ⚠ **The payola defense is the classifier's `isFree` field and it was already there**: pay-to-submit
	 * sync libraries are this lane's SubmitHub, and a page that asks an artist for an upfront listing fee
	 * is rejected before it ever becomes an outlet row. Do not add a query that seeks them out to
	 * "filter them later" - the cheapest place to not have them is to not fetch them.
	 *
	 * ⚠ **Neither pack takes a `{genre}` template, and that is a decision about THIS mechanism rather
	 * than about the queries.** `packPlaceholders` returns `needsGenres` when ANY query mentions
	 * `{genre}`, and the Discover page then refuses the WHOLE pack until a genre is selected - so one
	 * templated query would have made four perfectly good genre-free searches unrunnable on the first
	 * click, in the lane an owner is most likely to be trying blind. (`short_form_creators` has that
	 * shape and has been half-disabled since it shipped; filed at §I.5.) It also happens to be the
	 * honest answer for supervisors: they are found by the productions they work on, not by a genre
	 * they cover.
	 */
	{
		id: "sync_supervisors",
		labelKey: "discover.pack.syncSupervisors",
		queries: [
			"music supervisor accepting music submissions email",
			"music supervisor submit music for film and tv contact",
			"sync licensing submissions email independent artists",
			"music supervision company submit your music",
			"music supervisor song submissions contact form"
		]
	},
	{
		id: "music_libraries",
		labelKey: "discover.pack.musicLibraries",
		queries: [
			"production music library submit tracks email",
			"sync library accepting submissions from independent artists",
			"music licensing library submit your catalog contact",
			"royalty free music library artist submissions email",
			"music library composer submissions email"
		]
	},
	// {genre} / {region} placeholders expand against the Discover page's current
	// genre/region selections - see expandPackQueries in shared/discoveryPacks.ts
	{
		id: "genre_outlets",
		labelKey: "discover.pack.genreOutlets",
		queries: [
			"{genre} music blog submit music email",
			"best {genre} blogs accepting submissions",
			"{genre} music review submit demo",
			"{genre} radio show submit track"
		]
	},
	{
		id: "regional_outlets",
		labelKey: "discover.pack.regionalOutlets",
		queries: [
			"music blog {region} submit music email",
			"{region} music magazine submissions",
			"radio station {region} submit song independent artist",
			"{region} music newsletter submit"
		]
	}
];

/** Music genres and subgenres for scraper targeting */
export const GENRES: Record<string, string[]> = {
	Alternative: ["Alternative Rock", "Art Rock", "Britpop", "Dream Pop", "Grunge", "Indie Rock", "New Wave", "Post-Punk", "Shoegaze", "Noise Rock"],
	Blues: ["Acoustic Blues", "Chicago Blues", "Country Blues", "Delta Blues", "Electric Blues", "Soul Blues"],
	Classical: ["Baroque", "Chamber Music", "Contemporary Classical", "Opera", "Orchestral", "Romantic"],
	Country: ["Alt-Country", "Americana", "Bluegrass", "Country Pop", "Country Rock", "Outlaw Country", "Western"],
	Electronic: [
		"Ambient",
		"Breakbeat",
		"Chillwave",
		"Downtempo",
		"Drum & Bass",
		"Dubstep",
		"EDM",
		"Electro",
		"Future Bass",
		"Garage",
		"Glitch",
		"House",
		"IDM",
		"Lo-Fi",
		"Synthwave",
		"Techno",
		"Trance",
		"UK Garage",
		"Vaporwave"
	],
	Folk: ["Acoustic", "Alt-Folk", "Celtic", "Folk Pop", "Folk Rock", "Indie Folk", "Singer-Songwriter", "Traditional Folk"],
	Funk: ["Boogie", "Disco", "Funk Rock", "G-Funk", "P-Funk"],
	"Hip-Hop / Rap": [
		"Boom Bap",
		"Cloud Rap",
		"Conscious Hip-Hop",
		"Drill",
		"Experimental Hip-Hop",
		"Grime",
		"Lo-Fi Hip-Hop",
		"Old School Hip-Hop",
		"Trap",
		"Underground Hip-Hop"
	],
	Jazz: [
		"Acid Jazz",
		"Avant-Garde Jazz",
		"Bebop",
		"Cool Jazz",
		"Free Jazz",
		"Fusion",
		"Latin Jazz",
		"Neo-Soul Jazz",
		"Nu-Jazz",
		"Smooth Jazz",
		"Swing"
	],
	Latin: ["Bachata", "Bossa Nova", "Cumbia", "Latin Pop", "Latin Trap", "Reggaeton", "Salsa", "Samba", "Tropical"],
	Metal: [
		"Black Metal",
		"Death Metal",
		"Doom Metal",
		"Groove Metal",
		"Heavy Metal",
		"Metalcore",
		"Nu Metal",
		"Post-Metal",
		"Power Metal",
		"Progressive Metal",
		"Sludge Metal",
		"Stoner Metal",
		"Thrash Metal"
	],
	Pop: [
		"Alt Pop",
		"Art Pop",
		"Bubblegum Pop",
		"Chamber Pop",
		"Dance Pop",
		"Dark Pop",
		"Dream Pop",
		"Electropop",
		"Hyperpop",
		"Indie Pop",
		"K-Pop",
		"Pop-Rock",
		"Power Pop",
		"Progressive Pop",
		"Synth Pop",
		"Teen Pop"
	],
	Punk: ["Emo", "Hardcore", "Pop Punk", "Post-Hardcore", "Punk Rock", "Screamo", "Skate Punk"],
	"R&B / Soul": ["Contemporary R&B", "Funk", "Motown", "Neo-Soul", "New Jack Swing", "Quiet Storm", "Soul"],
	Reggae: ["Dancehall", "Dub", "Ragga", "Reggae Fusion", "Reggae Rock", "Roots Reggae", "Ska"],
	Rock: [
		"Classic Rock",
		"Garage Rock",
		"Glam Rock",
		"Hard Rock",
		"Indie Rock",
		"Math Rock",
		"Post-Rock",
		"Progressive Rock",
		"Psychedelic Rock",
		"Soft Rock",
		"Southern Rock",
		"Stoner Rock",
		"Surf Rock"
	],
	World: [
		"Afrobeats",
		"Arabic",
		"Bollywood",
		"Brazilian",
		"Calypso",
		"Celtic",
		"Fado",
		"Flamenco",
		"Highlife",
		"Indian Classical",
		"K-Pop",
		"Klezmer",
		"Polka",
		"Qawwali",
		"Soca",
		"Zouk"
	],
	Experimental: ["Avant-Garde", "Dark Ambient", "Drone", "Field Recording", "Glitch", "Industrial", "Musique Concrète", "Noise"]
};

/** Markets / geographic regions */
export const REGIONS: Record<string, string[]> = {
	"North America": ["United States", "Canada"],
	"United Kingdom": ["England", "Scotland", "Wales", "Northern Ireland"],
	"Western Europe": ["Germany", "France", "Netherlands", "Belgium", "Austria", "Switzerland"],
	"Northern Europe / Scandinavia": ["Sweden", "Norway", "Denmark", "Finland", "Iceland"],
	"Southern Europe": ["Spain", "Italy", "Portugal", "Greece"],
	"Eastern Europe": ["Poland", "Czech Republic", "Hungary", "Romania", "Bulgaria"],
	"Australia & New Zealand": ["Australia", "New Zealand"],
	"Latin America": ["Mexico", "Brazil", "Argentina", "Colombia", "Chile"],
	"East Asia": ["Japan", "South Korea", "China", "Taiwan"],
	"Southeast Asia": ["Philippines", "Indonesia", "Thailand", "Vietnam", "Malaysia", "Singapore"],
	"South Asia": ["India", "Pakistan", "Bangladesh", "Sri Lanka"],
	"Middle East": ["UAE", "Israel", "Turkey", "Lebanon", "Saudi Arabia"],
	Africa: ["Nigeria", "South Africa", "Kenya", "Ghana", "Tanzania", "Ethiopia"]
};

/** Flatten genre map into a sorted list of all selectable items (parent + subgenres) */
export function getAllGenreOptions(): { group: string; items: string[] }[] {
	return Object.entries(GENRES).map(([group, items]) => ({ group, items: [group, ...items] }));
}

/** Flatten region map into a sorted list of all selectable items */
export function getAllRegionOptions(): { group: string; items: string[] }[] {
	return Object.entries(REGIONS).map(([group, items]) => ({ group, items }));
}

/**
 * Languages an outlet can publish in, as offered on Discover. Full English names - WIRE DATA, not
 * chrome: this is the form the scraper stores in `blogs.language` and the form the AI is told to
 * answer in, so a translated value would stop matching what is already in the column.
 *
 * Keep it in step with `TARGET_LOCALES` (`shared/languages.ts`): a language you can translate a
 * press kit into is one whose outlets you'll want to find, so every target locale appears here
 * plus English. It stays a literal list rather than deriving from `languageName()` because these
 * are stored VALUES - deriving them would let an Intl or ICU change silently rewrite the vocabulary
 * a column already holds.
 */
export const OUTLET_LANGUAGES = [
	"English",
	"Danish",
	"German",
	"French",
	"Spanish",
	"Italian",
	"Portuguese",
	"Dutch",
	"Swedish",
	"Norwegian",
	"Finnish",
	"Polish",
	"Japanese",
	"Korean"
];

/** The outlet languages as one selectable group, for the Discover multiselect */
export function getAllLanguageOptions(): { group: string; items: string[] }[] {
	return [{ group: "Languages", items: OUTLET_LANGUAGES }];
}

/**
 * The `{{placeholder}}` variables an outreach template can use, in the order the cheat-sheet lists
 * them (outlet → curator → artist/release → credits → links → assets → related content).
 *
 * This array is the whole declaration. Each name's human DESCRIPTION - the cheat-sheet line the
 * owner reads while writing a template - is a `t()` key derived from the name
 * (`templateVariableDescKey`), so there is no second list to keep in step and no English sentence
 * living out here where the i18n audit cannot see it. Until round 122 this was a
 * `Record<name, "Outlet name (blog, station, magazine, channel…)">`: 92 English sentences rendered
 * into a fully translated app through an interpolation, invisible to every gate the repo has.
 *
 * The NAMES are wire data and stay English in every locale: they are what the owner types between
 * the braces and what `emailComposer` fills, and `ai.ts` lists them to the model when it scores a
 * template. Only the descriptions are chrome.
 */
export const TEMPLATE_VARIABLE_KEYS = [
	"outlet_name",
	"editor_name",
	"editor_first_name",
	"milestone",
	"curator_name",
	"playlist_name",
	"playlist_names",
	"playlist_followers",
	"playlist_count",
	"outlet_url",
	"artist_name",
	"release_title",
	"release_type",
	"genre",
	"primary_genre",
	"moods",
	"primary_mood",
	"instruments",
	"primary_instrument",
	"press_kit_url",
	"epk_url",
	"streaming_links",
	"personalized_intro",
	"submission_guidelines",
	"subject_format",
	"artist_origin",
	"current_location",
	"ean_upc",
	"isrc",
	"release_date",
	"recording_year",
	"lyrics",
	"duration",
	"bpm",
	"album_title",
	"producer",
	"composed_by",
	"recorded_by",
	"mixed_by",
	"mastered_by",
	"label",
	"collaborators",
	"response_email",
	"phone_number",
	"spotify",
	"apple_music",
	"soundcloud",
	"deezer",
	"youtube",
	"instagram",
	"tiktok",
	"facebook",
	"youtube_social",
	"spotify_social",
	"apple_music_social",
	"soundcloud_social",
	"deezer_social",
	"smart_link_url",
	"tagged_link_url",
	"fan_kit_url",
	"promo_blurb_long",
	"promo_blurb_short",
	"about",
	"artist_bio",
	"artist_bio_short",
	"artist_press_highlights",
	"recording_studio",
	"recording_equipment",
	// §I.5's clearance line (round 211). Three variables and no composed `{{clearance}}` sentence: a
	// server-built "One-stop: yes. Publisher: X." would be English prose assembled out of fragments,
	// which is the one thing the i18n rules forbid outright - the owner writes the sentence in their
	// own template, in their own language, and these fill the blanks in it.
	"rights_one_stop",
	"publisher",
	"writer_splits",
	// §I.5's second sync prerequisite (round 212): the supervisor's next question after clearance.
	// ⚠ Empty unless the file is genuinely reachable - it exists, the owner consented to publish it,
	// and the press kit is on the hub. A `{{#if instrumental_url}}` block therefore says nothing at
	// all rather than offering a link that 404s, which is the same discipline `fan_kit_url` follows.
	"instrumental_url",
	"site_type",
	"download_mp3_url",
	"download_wav_url",
	"song_genre",
	"song_subgenres",
	"song_moods",
	"song_instruments",
	"song_vocals",
	"song_tempo",
	"song_energy",
	"song_sound",
	"explicit_status",
	"related_links",
	"live_session_url",
	"live_session_title",
	"music_video_url",
	"music_video_title",
	"acoustic_version_url",
	"acoustic_version_title",
	"lyric_video_url",
	"lyric_video_title",
	"interview_url",
	"interview_title",
	"other_url",
	"other_title"
];

/**
 * A variable's description key. One place, so the convention cannot half-land in a rename, and so
 * `tests/i18n.test.ts` can gate the whole family exhaustively - it derives the key for every name
 * and asserts both locales carry it, which no regex scan over source could promise.
 */
export function templateVariableDescKey(name: string): string {
	return `tplvar.${name}`;
}

/**
 * The longest a `tplvar.*` description's NAME half may be, in characters.
 *
 * ⚠ **This exists because the rich editor puts that half inside a SENTENCE** (§AE round 4). A chip
 * reading "The editor/host the pitch is addressed to (when one was matched)" in the middle of
 * "Hi …," is not a friendlier rendering of `{{editor_name}}`, it is a longer one - the whole line
 * stops being readable as prose, which is the exact thing the chips were added to fix.
 */
export const TEMPLATE_VARIABLE_LABEL_MAX = 30;

/**
 * How long the SHORT artist bio may be, in characters.
 *
 * ⚠ **It is here rather than beside its one server reader because two sides have to agree on it now**
 * (2026-08-13, the Short artist bio's Autofill). `services/artistAutofill.ts` uses it to decide
 * whether a connected account's bio is short enough to offer at all, and the artist modal's Autofill
 * sends it to `POST /api/ai/shorten` as the length to write to. A number that decides both what the
 * app will ACCEPT and what it ASKS FOR, spelled twice, is a pair that agrees until one of them is
 * tuned - and the failure is a button whose answer is silently rejected by the field it fills.
 */
export const ARTIST_BIO_SHORT_MAX = 300;

/**
 * The short NAME a placeholder chip carries, out of its full description.
 *
 * ⚠ **The convention it depends on is that a description LEADS with the thing's name and qualifies
 * it in parentheses afterwards** - "Playlist followers (of the best-fitting playlist)". That was not
 * true of eleven of them until §AE round 4, and it is now gated in `tests/i18n.test.ts` rather than
 * merely fixed, because the copy lives in a dictionary nobody re-reads and the next description is
 * written by whoever adds the next variable, in whichever locale they think in.
 *
 * ⚠ **A second key family per variable was the obvious alternative and it is worse.** Ninety-seven
 * names times every locale is a hundred and ninety-four strings to keep in step, most of them one
 * word different from the description beside them - and a partial locale is exactly where such a
 * string hides, falling back to English inside an otherwise translated sentence.
 */
export function templateVariableLabel(description: string): string {
	// ⚠ The en dash is in here because `de.json` writes its asides with one. A separator list built
	// from the source language alone is the shape of blind spot this repo has met before: the rule
	// looks satisfied in English and the whole clause survives into a chip in German.
	const head = description.split(/ \(| - | – /)[0] ?? description;
	return head.length > 0 ? head : description;
}

/**
 * Curated Google Fonts selectable for a release's press kit. An allowlist by design:
 * the chosen family name is written into a fonts.googleapis.com URL, so free text
 * would be an injection vector. No selection = the current system font stack.
 */
export const EPK_FONTS = [
	// Sans-serif
	"Inter",
	"DM Sans",
	"Work Sans",
	"Manrope",
	"Sora",
	"Space Grotesk",
	"IBM Plex Sans",
	"Montserrat",
	"Poppins",
	"Outfit",
	"Raleway",
	"Nunito",
	"Rubik",
	"Karla",
	"Jost",
	"Figtree",
	"Lexend",
	"Plus Jakarta Sans",
	"Archivo",
	"Barlow",
	"Urbanist",
	// Serif
	"Lora",
	"Playfair Display",
	"Source Serif 4",
	"Spectral",
	"Merriweather",
	"EB Garamond",
	"Cormorant Garamond",
	"Libre Baskerville",
	"Crimson Pro",
	"Fraunces",
	"Bitter",
	"Zilla Slab",
	"DM Serif Display",
	// Display
	"Oswald",
	"Josefin Sans",
	"Syne",
	"Unbounded",
	"Comfortaa",
	"Bebas Neue",
	"Anton",
	"Abril Fatface",
	"Righteous",
	// Mono
	"JetBrains Mono",
	"IBM Plex Mono",
	"Space Mono"
] as const;
export type EpkFont = (typeof EPK_FONTS)[number];

/**
 * Google Fonts css2 weight spec per family, where it differs from the default
 * 400;600;700. The css2 API rejects the WHOLE request when any listed weight is
 * missing from the family, so families without 600/700 must declare exactly what
 * they ship (the browser synthesizes bold for 400-only display faces - fine at
 * heading sizes).
 */
export const EPK_FONT_WEIGHTS: Partial<Record<EpkFont, string>> = {
	Merriweather: "400;700",
	"Libre Baskerville": "400;700",
	"DM Serif Display": "400",
	"Bebas Neue": "400",
	Anton: "400",
	"Abril Fatface": "400",
	Righteous: "400",
	"Space Mono": "400;700"
};

/**
 * Dropdown grouping for the appearance picker - same names, easier to scan.
 *
 * The four group names stay English: they are typographic classifications, and Danish (like most
 * of Europe) uses the same words - "sans-serif", "serif", "display", "mono" are the terms, not
 * translations of them. Same call as the reel weight names ("SemiBold") and the audio formats.
 */
export const EPK_FONT_GROUPS: { label: string; fonts: readonly EpkFont[] }[] = [
	{
		// i18n-ignore: typographic classification, the same word in every locale
		label: "Sans-serif",
		fonts: [
			"Inter",
			"DM Sans",
			"Work Sans",
			"Manrope",
			"Sora",
			"Space Grotesk",
			"IBM Plex Sans",
			"Montserrat",
			"Poppins",
			"Outfit",
			"Raleway",
			"Nunito",
			"Rubik",
			"Karla",
			"Jost",
			"Figtree",
			"Lexend",
			"Plus Jakarta Sans",
			"Archivo",
			"Barlow",
			"Urbanist"
		]
	},
	{
		// i18n-ignore: typographic classification, the same word in every locale
		label: "Serif",
		fonts: [
			"Lora",
			"Playfair Display",
			"Source Serif 4",
			"Spectral",
			"Merriweather",
			"EB Garamond",
			"Cormorant Garamond",
			"Libre Baskerville",
			"Crimson Pro",
			"Fraunces",
			"Bitter",
			"Zilla Slab",
			"DM Serif Display"
		]
	},
	{
		// i18n-ignore: typographic classification, the same word in every locale
		label: "Display",
		fonts: ["Oswald", "Josefin Sans", "Syne", "Unbounded", "Comfortaa", "Bebas Neue", "Anton", "Abril Fatface", "Righteous"]
	},
	// i18n-ignore: typographic classification, the same word in every locale
	{ label: "Mono", fonts: ["JetBrains Mono", "IBM Plex Mono", "Space Mono"] }
];

/** Types of alternative content that can be associated with a release */
export const RELATED_CONTENT_TYPES = ["live_session", "music_video", "acoustic_version", "lyric_video", "interview", "other"] as const;
export type RelatedContentType = (typeof RELATED_CONTENT_TYPES)[number];

export interface RelatedContentEntry {
	type: RelatedContentType;
	title: string;
	url: string;
}

/**
 * The same six types have TWO readers, so they get two answers - the clearest small example of
 * this file's split.
 *
 * These English labels are OUTLET-facing: `utils/relatedContent.ts` uses one as the fallback title
 * of an untitled link in the `{{related_links}}` template variable, which lands inside a pitch
 * email. They belong to the outreach language axis (today always English there), not to `t()` -
 * the labels the OWNER reads are `RELATED_CONTENT_TYPE_LABEL_KEYS` below.
 */
export const RELATED_CONTENT_TYPE_LABELS: Record<RelatedContentType, string> = {
	live_session: "Live session",
	music_video: "Music video",
	acoustic_version: "Acoustic version",
	lyric_video: "Lyric video",
	interview: "Interview",
	other: "Other"
};

/** The owner-facing labels - the release editor's type dropdown, rendered through `t()`. */
export const RELATED_CONTENT_TYPE_LABEL_KEYS: Record<RelatedContentType, string> = {
	live_session: "related.type.liveSession",
	music_video: "related.type.musicVideo",
	acoustic_version: "related.type.acousticVersion",
	lyric_video: "related.type.lyricVideo",
	interview: "related.type.interview",
	other: "related.type.other"
};

/**
 * Link-in-bio page building blocks, shared by the renderer (bioPage.ts) and the
 * studio (frontend artists.ts) so both enumerate exactly the same links. The
 * social key doubles as the link's stable bio_theme key (releases use
 * `release-<id>`, custom rows `custom-<index>`).
 */
/**
 * Which layout a link-in-bio page uses when nobody has chosen one (owner call, 2026-08-07: *"the Link
 * in Bio should default to using the 'Cards' layout"*).
 *
 * ⚠ **It lives in `shared/` because the default was spelled in THREE places**, which is CLAUDE.md's
 * fork rule in its most ordinary form: `parseBioTheme()` fell back to `null` (which the renderer read
 * as stack), the studio island declared `accessor layout = "stack"` for a page nobody had saved yet,
 * and the same island fell back to `"stack"` again when reading a saved theme. Each was locally
 * reasonable and reviewing any one of them alone could not have found the disagreement - so flipping
 * the default meant editing three files, and the next round would have to find all three again.
 *
 * ⚠ **This is WIRE DATA, not a label.** The value is stored in `artists.bio_theme` and matched by the
 * renderer, so it never goes through `t()` - the reader-facing name for it is `artists.layoutCard`,
 * which is a different string in a different file on purpose. See this file's header for the three
 * kinds.
 *
 * ⚠ **Changing it moves EXISTING pages.** A bio page whose theme never named a layout is stored as
 * "unset", not as "stack", so it follows this constant - which is what a default means, and is worth
 * knowing before changing it again.
 */
export const BIO_DEFAULT_LAYOUT: "stack" | "horizontal" = "horizontal";

export const BIO_SOCIAL_LABELS: [string, string][] = [
	["instagram", "Instagram"],
	["tiktok", "TikTok"],
	["youtube", "YouTube"],
	["facebook", "Facebook"],
	["spotify", "Spotify"],
	["apple_music", "Apple Music"],
	["soundcloud", "SoundCloud"],
	["deezer", "Deezer"]
];

// `BIO_STREAMING_FALLBACK` used to live here: the five keys below, in the order below, under a name
// that made them look like a decision about the link-in-bio page. They were not - they were a copy,
// and round 168 found it had drifted (no Spotify album-id fallback, so a released song whose only
// home is its S4A album page rendered as dead text on the bio page while its own smart link drew a
// working button). ⚠ **A duplicated list wearing its own name is the hardest kind to notice**: round
// 166 unified four modules that answered "where can a fan stream this?" in logic and walked straight
// past this one, because a named constant reads as something somebody chose. `shared/streaming.ts`
// is the whole answer now - `primaryDestination()` for a surface with room for one link.

/**
 * Streaming platforms offered as buttons on the smart link, in display order. The renderer
 * (linkPage.ts) owns the brand icons and verbs; this key→label list is shared with the release
 * workspace so the "which services to show" control enumerates exactly the same platforms in the
 * same order. Keys match the release's `streaming_links` keys.
 */
export const SMART_LINK_PLATFORMS: [string, string][] = [
	["spotify", "Spotify"],
	["apple_music", "Apple Music"],
	["youtube", "YouTube"],
	["soundcloud", "SoundCloud"],
	["deezer", "Deezer"]
];

// Locale display names used to live here as a hand-written English catalog (`LOCALE_NAMES`).
// They are `shared/languages.ts` now: the code list is the only data we own, and every NAME comes
// from `Intl.DisplayNames` in the reader's locale - see that file for why hand-translating it was
// never the alternative.

/**
 * What the owner types to unlock the irreversible "delete everything" button - the ONE string this
 * app asks a person to reproduce exactly, so both sides have to read it from here.
 *
 * ⚠ **It is WIRE DATA, not chrome: it does NOT go through `t()`** (constants.ts's three-kinds rule at
 * the top of this file). The server compares what arrived against this literal, so a Danish UI that
 * asked for `SLET` would collect a word the route can only reject - the label around it translates,
 * the token never does.
 *
 * ⚠ **It replaced the WORKSPACE NAME on 2026-08-10, on an owner report, and the reason generalizes
 * past this button.** The gate used to ask for the workspace's own name and the label read *"Type
 * the workspace name (Frederik Wessberg's workspace) to unlock this"* - which names one thing and
 * then demands a different, longer one, containing an apostrophe that a phone keyboard will happily
 * turn into a curly quote the comparison rejects. **A confirmation whose answer the reader has to
 * derive is a puzzle, and a puzzle in front of a destructive action gets solved by copy-paste**,
 * which is the reflex the gate exists to interrupt. The point was never the name; it was typing
 * something deliberately. A short fixed token does that and can be read off the label with no
 * derivation at all.
 */
export const DELETE_CONFIRM_WORD = "DELETE";
